import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import Navbar from '../components/Navbar';
import { GoldBtn } from '../components/UI';
import {
  getAllActiveBattles, submitBattleVote,
  getBattleLeaderboard, getBattleSuggestions,
  submitBattleSuggestion, deleteBattleSuggestion,
} from '../lib/supabase';

// ── Single Battle Card ────────────────────────────────────────
function BattleFilmCard({ film, votes, totalVotes, selected, onClick, hasVoted }) {
  const hasPoster = film?.poster_url && film.poster_url.startsWith('http');
  const pct = totalVotes ? Math.round(votes / totalVotes * 100) : 0;
  const isWinner = hasVoted && votes === Math.max(...[votes]);

  return (
    <button onClick={onClick} disabled={hasVoted} style={{
      flex: 1, minWidth: 0, background: 'var(--surface)',
      border: `2px solid ${selected ? 'var(--gold)' : hasVoted && !selected ? 'var(--border)' : 'var(--border2)'}`,
      borderRadius: 16, overflow: 'hidden', cursor: hasVoted ? 'default' : 'pointer',
      transition: 'all 0.25s',
      transform: selected ? 'scale(1.02)' : hasVoted && !selected ? 'scale(0.97)' : 'scale(1)',
      boxShadow: selected ? '0 0 40px rgba(212,175,55,0.2)' : 'none',
      opacity: hasVoted && !selected ? 0.65 : 1,
      display: 'flex', flexDirection: 'column', padding: 0,
    }}
      onMouseEnter={e => { if (!hasVoted) { e.currentTarget.style.borderColor='var(--gold-dim)'; e.currentTarget.style.transform='scale(1.02)'; }}}
      onMouseLeave={e => { if (!hasVoted && !selected) { e.currentTarget.style.borderColor='var(--border2)'; e.currentTarget.style.transform='scale(1)'; }}}
    >
      {/* Poster */}
      <div style={{ width: '100%', paddingTop: '130%', position: 'relative', overflow: 'hidden', background: 'var(--surface2)' }}>
        <div style={{ position: 'absolute', inset: 0 }}>
          {hasPoster ? (
            <img src={film.poster_url} alt={film.title}
              style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top', display: 'block' }} />
          ) : (
            <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'linear-gradient(135deg, #0d0d14, #1a1a28)', fontSize: 64 }}>
              {film.poster_emoji || '🎬'}
            </div>
          )}
          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '60%',
            background: 'linear-gradient(to top, rgba(4,4,6,0.97), transparent)' }} />
          {selected && (
            <div style={{ position: 'absolute', top: 12, right: 12, background: 'var(--gold)',
              borderRadius: '50%', width: 44, height: 44, display: 'flex', alignItems: 'center',
              justifyContent: 'center', fontSize: 22, boxShadow: '0 0 20px rgba(212,175,55,0.6)' }}>👑</div>
          )}
          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '12px 14px' }}>
            <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(13px,2vw,17px)',
              fontWeight: 700, color: '#fff', marginBottom: 3, lineHeight: 1.2,
              textShadow: '0 2px 8px rgba(0,0,0,0.9)' }}>{film.title}</div>
            <span style={{ background: 'rgba(212,175,55,0.2)', border: '1px solid rgba(212,175,55,0.3)',
              borderRadius: 4, padding: '1px 7px', fontSize: 9,
              fontFamily: "'DM Mono', monospace", color: 'var(--gold)', letterSpacing: 1 }}>
              {film.language?.toUpperCase()}
            </span>
          </div>
        </div>
      </div>

      {/* Vote result bar */}
      {hasVoted ? (
        <div style={{ padding: '10px 14px', background: selected ? 'rgba(212,175,55,0.07)' : 'var(--surface)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
            <span style={{ color: selected ? 'var(--gold)' : 'var(--text-muted)', fontSize: 13 }}>
              {selected ? '✓ Your pick' : 'Not selected'}
            </span>
            <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 13, color: selected ? 'var(--gold)' : 'var(--text-muted)', fontWeight: 700 }}>
              {pct}%
            </span>
          </div>
          <div style={{ height: 6, background: 'var(--surface2)', borderRadius: 3, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${pct}%`, borderRadius: 3, transition: 'width 0.6s ease',
              background: selected ? 'var(--gold)' : 'var(--border2)' }} />
          </div>
          <div style={{ color: 'var(--text-muted)', fontSize: 11, marginTop: 4, fontFamily: "'DM Mono', monospace" }}>
            {votes} vote{votes !== 1 ? 's' : ''}
          </div>
        </div>
      ) : (
        <div style={{ padding: '10px 14px', textAlign: 'center' }}>
          <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>👆 Tap to vote</span>
        </div>
      )}
    </button>
  );
}

// ── Single Battle Block ───────────────────────────────────────
function BattleBlock({ battle, userId, onVoted, isAdmin }) {
  const votes    = battle.battle_votes || [];
  const myVote   = votes.find(v => v.user_id === userId);
  const votesA   = votes.filter(v => v.winner_id === battle.film_a_id).length;
  const votesB   = votes.filter(v => v.winner_id === battle.film_b_id).length;
  const total    = votes.length;
  const [voting, setVoting] = useState(false);

  const handleVote = async (winnerId) => {
    if (!userId || myVote || voting) return;
    setVoting(true);
    await submitBattleVote(battle.id, userId, winnerId);
    setVoting(false);
    onVoted();
  };

  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border2)',
      borderRadius: 18, overflow: 'hidden', marginBottom: 24 }}>

      {/* Battle header */}
      <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 16 }}>⚔️</span>
          {battle.is_featured && (
            <span style={{ background: 'rgba(212,175,55,0.12)', border: '1px solid var(--gold-dim)',
              borderRadius: 4, padding: '2px 8px', fontSize: 10,
              fontFamily: "'DM Mono', monospace", color: 'var(--gold)', letterSpacing: 1 }}>FEATURED</span>
          )}
          <span style={{ color: 'var(--text-muted)', fontSize: 12, fontFamily: "'DM Mono', monospace" }}>
            {new Date(battle.battle_date || battle.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
          </span>
        </div>
        <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, color: 'var(--text-muted)' }}>
          {total} vote{total !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Cards */}
      <div style={{ padding: '16px', display: 'flex', gap: 12, alignItems: 'stretch' }}>
        <BattleFilmCard
          film={battle.film_a} votes={votesA} totalVotes={total}
          selected={myVote?.winner_id === battle.film_a_id}
          onClick={() => handleVote(battle.film_a_id)}
          hasVoted={!!myVote}
        />
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          gap: 6, flexShrink: 0 }}>
          <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(14px,2.5vw,20px)',
            fontWeight: 900, color: 'var(--gold)', letterSpacing: 4,
            textShadow: '0 0 20px rgba(212,175,55,0.4)' }}>VS</div>
        </div>
        <BattleFilmCard
          film={battle.film_b} votes={votesB} totalVotes={total}
          selected={myVote?.winner_id === battle.film_b_id}
          onClick={() => handleVote(battle.film_b_id)}
          hasVoted={!!myVote}
        />
      </div>

      {/* After voting message */}
      {myVote && (
        <div style={{ padding: '0 16px 14px', textAlign: 'center' }}>
          <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>
            You voted · {total} total vote{total !== 1 ? 's' : ''}
          </span>
        </div>
      )}
    </div>
  );
}

// ── Main BattlePage ───────────────────────────────────────────
export default function BattlePage({ onAuthOpen, onAdminClick, onChatClick, onBattleClick, onRankingsClick, onTasteClick }) {
  const { user, profile } = useAuth();
  const [battles,     setBattles]     = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [tab,         setTab]         = useState('battles'); // battles | leaderboard | suggestions
  const [sugText,     setSugText]     = useState('');
  const [submitting,  setSubmitting]  = useState(false);
  const [sugMsg,      setSugMsg]      = useState('');
  const isAdmin = profile?.role === 'admin';

  const load = useCallback(async () => {
    setLoading(true);
    const [b, lb, s] = await Promise.all([
      getAllActiveBattles(), getBattleLeaderboard(), getBattleSuggestions()
    ]);
    setBattles(b); setLeaderboard(lb); setSuggestions(s);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSuggest = async () => {
    if (!user) return onAuthOpen();
    const text = sugText.trim();
    if (!text) return;
    setSubmitting(true);
    await submitBattleSuggestion(user.id, text);
    setSugText(''); setSugMsg('✓ Suggestion submitted!');
    setTimeout(() => setSugMsg(''), 3000);
    await load();
    setSubmitting(false);
  };

  const handleDeleteSug = async (id) => {
    await deleteBattleSuggestion(id);
    load();
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--black)', display: 'flex', flexDirection: 'column' }}>
      <Navbar onAuthOpen={onAuthOpen} onAdminClick={onAdminClick} onChatClick={onChatClick}
        onBattleClick={onBattleClick} onRankingsClick={onRankingsClick} onTasteClick={onTasteClick} />

      {/* Hero */}
      <div style={{ background: 'linear-gradient(180deg, #07070f 0%, var(--black) 100%)',
        padding: 'clamp(24px,4vw,48px) 16px 24px', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
          width: 500, height: 220, background: 'radial-gradient(ellipse, rgba(212,175,55,0.07) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <div style={{ position: 'relative' }}>
          <div style={{ fontSize: 'clamp(28px,5vw,48px)', marginBottom: 6 }}>⚔️</div>
          <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(24px,4vw,40px)',
            fontWeight: 900, color: 'var(--text)', marginBottom: 8 }}>Battle Mode</h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 'clamp(13px,2vw,15px)', maxWidth: 380, margin: '0 auto' }}>
            Vote on today's film battles. Suggest your own. See who wins.
          </p>
        </div>
      </div>

      <div style={{ flex: 1, maxWidth: 860, width: '100%', margin: '0 auto', padding: '16px 16px 40px' }}>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', marginBottom: 24 }}>
          {[
            ['battles',     `⚔️ Battles (${battles.length})`],
            ['leaderboard', '🏆 Leaderboard'],
            ['suggestions', `💡 Suggest (${suggestions.length})`],
          ].map(([t, label]) => (
            <button key={t} onClick={() => setTab(t)} style={{
              padding: '10px 18px', background: 'none', border: 'none', cursor: 'pointer',
              borderBottom: tab === t ? '2px solid var(--gold)' : '2px solid transparent',
              color: tab === t ? 'var(--gold)' : 'var(--text-muted)',
              fontSize: 14, fontFamily: "'Crimson Text', serif",
              fontWeight: tab === t ? 600 : 400, transition: 'all 0.15s',
            }}>{label}</button>
          ))}
        </div>

        {loading && (
          <div style={{ textAlign: 'center', padding: 60 }}>
            <div style={{ width: 40, height: 40, border: '3px solid var(--border)', borderTopColor: 'var(--gold)',
              borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto' }} />
          </div>
        )}

        {/* ── BATTLES TAB ── */}
        {!loading && tab === 'battles' && (
          <>
            {battles.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 20px', background: 'var(--surface)',
                borderRadius: 16, border: '1px solid var(--border)' }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>⚔️</div>
                <div style={{ color: 'var(--text-dim)', fontSize: 16, marginBottom: 8 }}>No battles today yet.</div>
                <div style={{ color: 'var(--text-muted)', fontSize: 14 }}>
                  {isAdmin ? 'Go to Admin → Battles to create today\'s battles.' : 'Check back later — the admin will post battles soon!'}
                </div>
              </div>
            ) : (
              battles.map(battle => (
                <BattleBlock key={battle.id} battle={battle} userId={user?.id} onVoted={load} isAdmin={isAdmin} />
              ))
            )}
            {!user && (
              <div style={{ textAlign: 'center', marginTop: 8 }}>
                <GoldBtn outline onClick={onAuthOpen}>Log In to Vote</GoldBtn>
              </div>
            )}
          </>
        )}

        {/* ── LEADERBOARD TAB ── */}
        {!loading && tab === 'leaderboard' && (
          <div>
            <p style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 20 }}>
              Films with the most battle wins across all time.
            </p>
            {leaderboard.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-muted)' }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>🏆</div>
                No battles voted on yet.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {leaderboard.map(({ film, wins }, idx) => {
                  const medals = ['🥇','🥈','🥉'];
                  const hasPoster = film?.poster_url?.startsWith('http');
                  return (
                    <div key={film.id} style={{ display: 'flex', alignItems: 'center', gap: 14,
                      background: idx < 3 ? `rgba(212,175,55,${0.07 - idx*0.015})` : 'var(--surface)',
                      border: `1px solid ${idx < 3 ? 'rgba(212,175,55,0.2)' : 'var(--border)'}`,
                      borderRadius: 12, padding: '12px 18px' }}>
                      <div style={{ width: 32, textAlign: 'center', fontSize: idx < 3 ? 22 : 14,
                        color: 'var(--text-muted)', fontFamily: "'DM Mono', monospace", fontWeight: 600 }}>
                        {idx < 3 ? medals[idx] : `#${idx+1}`}
                      </div>
                      <div style={{ width: 40, height: 54, borderRadius: 6, overflow: 'hidden',
                        background: 'var(--surface2)', flexShrink: 0 }}>
                        {hasPoster
                          ? <img src={film.poster_url} alt={film.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>{film.poster_emoji || '🎬'}</div>
                        }
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 15,
                          fontWeight: 700, color: 'var(--text)' }}>{film.title}</div>
                        <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>{film.language}</div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 18,
                          fontWeight: 700, color: 'var(--gold)' }}>{wins}</div>
                        <div style={{ color: 'var(--text-muted)', fontSize: 11 }}>win{wins !== 1 ? 's' : ''}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── SUGGESTIONS TAB ── */}
        {!loading && tab === 'suggestions' && (
          <div>
            <p style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 20 }}>
              Suggest a battle you'd love to see. The admin picks the best ones!
            </p>

            {/* Suggestion input */}
            <div style={{ background: 'var(--surface)', border: '1px solid var(--border2)',
              borderRadius: 14, padding: 18, marginBottom: 24 }}>
              <div style={{ color: 'var(--text-dim)', fontSize: 14, marginBottom: 10, fontWeight: 600 }}>
                💡 Suggest a battle
              </div>
              <textarea
                value={sugText} onChange={e => setSugText(e.target.value)}
                placeholder="e.g. KGF vs Bahubali — which is the bigger mass entertainer?"
                maxLength={300} rows={3}
                style={{ width: '100%', background: 'var(--surface2)', border: '1px solid var(--border2)',
                  borderRadius: 8, color: 'var(--text)', padding: '10px 14px', fontSize: 15,
                  fontFamily: "'Crimson Text', serif", lineHeight: 1.5, resize: 'none', outline: 'none' }}
                onFocus={e => e.target.style.borderColor = 'var(--gold-dim)'}
                onBlur={e => e.target.style.borderColor = 'var(--border2)'}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10 }}>
                <span style={{ color: 'var(--text-muted)', fontSize: 11, fontFamily: "'DM Mono', monospace" }}>
                  {sugText.length}/300
                </span>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  {sugMsg && <span style={{ color: 'var(--green)', fontSize: 13 }}>{sugMsg}</span>}
                  <GoldBtn small onClick={handleSuggest} disabled={submitting || !sugText.trim()}>
                    {submitting ? 'Sending…' : 'Submit'}
                  </GoldBtn>
                </div>
              </div>
            </div>

            {/* Suggestions list */}
            {suggestions.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-muted)' }}>
                No suggestions yet. Be the first!
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {suggestions.map(s => (
                  <div key={s.id} style={{ background: 'var(--surface)', border: '1px solid var(--border)',
                    borderRadius: 10, padding: '14px 16px', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                    <div style={{ fontSize: 20, flexShrink: 0 }}>💡</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ color: 'var(--text)', fontSize: 15, lineHeight: 1.5 }}>{s.suggestion}</div>
                      <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 4 }}>
                        by @{s.profiles?.username} · {new Date(s.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                      </div>
                    </div>
                    {(s.user_id === user?.id || isAdmin) && (
                      <button onClick={() => handleDeleteSug(s.id)} style={{ background: 'none', border: 'none',
                        color: 'var(--text-muted)', fontSize: 16, cursor: 'pointer', padding: '0 4px',
                        opacity: 0.5, transition: 'opacity 0.15s' }}
                        onMouseEnter={e => e.currentTarget.style.opacity = 1}
                        onMouseLeave={e => e.currentTarget.style.opacity = 0.5}
                      >🗑</button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
